import DS from 'ember-data';
import Route from 'cio/routes/base';
import moment from 'moment-timezone';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';

import EnvironmentService from 'cio/services/environment';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';
import Transition from '@ember/routing/-private/transition';
import ConfirmChangesService from 'cio/services/confirm-changes';
import ObjectModel from 'cio/models/object';
import NewObjectController from './controller';
import isDestroyed from 'cio/utils/is-destroyed';
import ObjectTypeModel from 'cio/models/object-type';
import { redirectIfNoPermission } from 'cio/utils/route-access';

export default class NewObjectRoute extends Route {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service confirmChanges!: ConfirmChangesService;

  crumb() {
    let objectType = this.modelFor('objects.type') as ObjectTypeModel;
    return {
      name: `Add ${objectType.singularName}`,
      path: 'objects.type.new',
      model: objectType.slug,
    };
  }

  model() {
    return this.store.createRecord('object', {
      attributes: {
        [ObjectIdentifierKey.ObjectId]: '',
        created_at: moment().unix(),
        name: '',
      },
      objectType: this.modelFor('objects.type'),
    });
  }

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    return redirectIfNoPermission(this, 'journeys/objects/create', transition);
  }

  deactivate() {
    super.deactivate();
    // needed to avoid memory leaks/errors in tests:
    if (isDestroyed(this.store)) {
      return;
    }

    let model = this.modelFor(this.routeName) as ObjectTypeModel;
    if (model == null || isDestroyed(model) || model.get('isDeleted') === true) {
      return;
    }

    model.rollbackAttributes();
  }

  setupController(controller: NewObjectController, model: ObjectModel, transition: Transition) {
    super.setupController(controller, model, transition);
    controller.prepareAttributesProxy();
    controller.objectType = this.modelFor('objects.type') as ObjectTypeModel;
    this.confirmChanges.register(controller, this);
  }

  @action
  willTransition() {
    let model = this.modelFor(this.routeName) as ObjectModel;

    // The record created on this page is temporary, since the back end creates new
    // objects via the identify queue. The actual record will be queried later, so it's safe to
    // unload this object record every time.
    if (!isDestroyed(model)) {
      model.unloadRecord();
    }
  }
}
